import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../Dialog";
import { SudoButton } from "../SudoButton";
import { cn } from "src/lib/cn";

export interface ConfirmationOptions {
  title: string;
  message: ReactNode;
  actionLabel?: string;
  destructive?: boolean;
  children?: ReactNode;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export interface ConfirmationDialogProps extends ConfirmationOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => unknown | Promise<unknown>;
  className?: string;
  pending?: boolean;
  confirmDisabled?: boolean;
  closeOnConfirm?: boolean;
  secondaryAction?: { label: string; onClick: () => unknown | Promise<unknown> };
}

/** App-controlled confirmations. Cancel is the safe initial keyboard target. */
export function ConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  message,
  actionLabel = "Confirm",
  destructive = false,
  children,
  className,
  returnFocusRef,
  pending = false,
  confirmDisabled = false,
  closeOnConfirm = true,
  secondaryAction,
}: ConfirmationDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const busy = submitting || pending;

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const dismiss = () => {
    if (!inFlight.current && !pending) onOpenChange(false);
  };

  const submit = async (action = onConfirm) => {
    // A ref also blocks clicks dispatched before React has rendered disabled.
    if (inFlight.current || pending || confirmDisabled) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await action();
      if (closeOnConfirm) onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Something went wrong. Please try again.");
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss();
      }}
    >
      <DialogContent
        hideClose
        overlayClassName="z-[2147483646]"
        data-confirmation-dialog=""
        aria-busy={busy}
        className={cn("z-[2147483647] w-[calc(100%-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg text-left", className)}
        onOpenAutoFocus={(event: Event) => {
          event.preventDefault();
          previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          cancelRef.current?.focus();
        }}
        onCloseAutoFocus={(event: Event) => {
          event.preventDefault();
          const target = returnFocusRef?.current?.isConnected ? returnFocusRef.current : previousFocus.current;
          if (target?.isConnected) target.focus();
        }}
        onEscapeKeyDown={(event: KeyboardEvent) => {
          if (busy) event.preventDefault();
        }}
        onInteractOutside={(event: Event) => {
          if (busy) event.preventDefault();
        }}
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
        onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="whitespace-pre-wrap break-words text-app-grey-0 leading-relaxed">{message}</DialogDescription>
        </DialogHeader>
        {children}
        {error && (
          <p
            role="alert"
            className="text-app-danger-color whitespace-pre-wrap"
          >
            {error}
          </p>
        )}
        <DialogFooter>
          {secondaryAction && (
            <SudoButton
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => submit(secondaryAction.onClick)}
            >
              {secondaryAction.label}
            </SudoButton>
          )}
          <SudoButton
            ref={cancelRef}
            type="button"
            variant="secondaryGrey"
            disabled={busy}
            onClick={dismiss}
          >
            Cancel
          </SudoButton>
          <SudoButton
            type="button"
            variant={destructive ? "dangerFilled" : "primary"}
            disabled={busy || confirmDisabled}
            onClick={() => submit()}
          >
            {busy ? "Working…" : actionLabel}
          </SudoButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
